import React from 'react';
import { Link } from 'react-router-dom';

export default function PriestTerms() {
  return (
    <div className="page">
      <div className="container" style={{ maxWidth: '860px', padding: '3rem 2rem' }}>
        <div className="page-header">
          <h1>Priest / Purohit Terms of Service</h1>
          <p>Last updated: May 27, 2026</p>
        </div>

        <div className="static-content">
          <section>
            <h2>1. Introduction</h2>
            <p>
              These Priest Terms of Service ("Priest Terms") govern the relationship between AstroA2Z
              ("Platform") and registered priests/purohits/pandits ("Priest", "you") who offer puja,
              ritual, and consultation services through the Platform. By registering as a Priest, you
              agree to these terms.
            </p>
          </section>

          <section>
            <h2>2. Relationship</h2>
            <p>
              You are an <strong>independent service provider</strong>, not an employee, agent, or
              partner of AstroA2Z. Nothing in these terms creates an employment, partnership, or
              agency relationship. You are responsible for your own taxes, insurance, and compliance
              with applicable laws.
            </p>
          </section>

          <section>
            <h2>3. Eligibility &amp; Verification</h2>
            <ul>
              <li>You must be at least 18 years of age</li>
              <li>You must provide accurate information about your qualifications, experience, and lineage</li>
              <li>AstroA2Z may verify your credentials but is not obligated to do so</li>
              <li>Misrepresentation of qualifications is grounds for immediate termination</li>
            </ul>
          </section>

          <section>
            <h2>4. Services &amp; Conduct</h2>
            <p>As a Priest on our Platform, you agree to:</p>
            <ul>
              <li>Perform booked pujas/rituals with sincerity and to the best of your ability</li>
              <li>Arrive on time for scheduled bookings or provide adequate advance notice of cancellation</li>
              <li>Treat all devotees/users with respect and dignity regardless of caste, gender, or background</li>
              <li>Not make guaranteed promises about outcomes of rituals or pujas</li>
              <li>Not provide medical, financial, or legal advice</li>
              <li>Not engage in any fraudulent, deceptive, or exploitative practices</li>
              <li>Maintain hygiene and professionalism during all interactions</li>
              <li>Not solicit users to transact outside the Platform to avoid commission</li>
            </ul>
          </section>

          <section>
            <h2>5. Prohibited Conduct</h2>
            <p>The following are strictly prohibited and will result in immediate termination:</p>
            <ul>
              <li>Demanding money beyond the Platform-agreed price (dakshina is acceptable only if voluntarily offered)</li>
              <li>Fear-mongering or exploiting users' religious sentiments for personal gain</li>
              <li>Claiming supernatural powers, guaranteed curse removal, or assured outcomes</li>
              <li>Any form of discrimination, harassment, or inappropriate behavior</li>
              <li>Sharing user personal data with any third party</li>
              <li>Performing rituals that are illegal under Indian law</li>
            </ul>
          </section>

          <section>
            <h2>6. Payments &amp; Commission</h2>
            <ul>
              <li>Payments from users are collected by AstroA2Z and disbursed to you after deducting
                the Platform commission</li>
              <li>Commission rate is communicated at the time of registration and may be revised with
                30 days' notice</li>
              <li>Payouts are processed weekly/bi-weekly to your registered bank account</li>
              <li>You are responsible for declaring this income and paying applicable taxes (GST, income tax)</li>
            </ul>
          </section>

          <section>
            <h2>7. Cancellation by Priest</h2>
            <ul>
              <li>Cancellations must be made at least 24 hours before the scheduled time</li>
              <li>Repeated cancellations (3+ in a month) may result in account suspension</li>
              <li>No-shows without prior notice will result in a penalty deduction from future payouts</li>
            </ul>
          </section>

          <section>
            <h2>8. Ratings &amp; Reviews</h2>
            <p>
              Users may rate and review your services. You agree not to solicit, coerce, or
              incentivize false reviews. AstroA2Z may remove reviews that violate our policies but
              is not obligated to remove genuine negative feedback.
            </p>
          </section>

          <section>
            <h2>9. Intellectual Property</h2>
            <p>
              Any content you create on the Platform (profile descriptions, photos, videos) grants
              AstroA2Z a non-exclusive, royalty-free license to display it on the Platform for
              service delivery purposes. You retain ownership of your content.
            </p>
          </section>

          <section>
            <h2>10. Data &amp; Privacy</h2>
            <p>
              You will have access to limited user data necessary for service delivery (name, booking
              details, location). You agree to:
            </p>
            <ul>
              <li>Use user data solely for performing the booked service</li>
              <li>Not store, share, or misuse any user personal data</li>
              <li>Delete any user data in your possession after service completion</li>
              <li>Comply with the DPDP Act, 2023 in handling any personal data</li>
            </ul>
          </section>

          <section>
            <h2>11. Liability &amp; Indemnity</h2>
            <p>
              You acknowledge that AstroA2Z is a technology platform facilitating connections and
              is not responsible for the quality, spiritual efficacy, or outcome of your services.
              You agree to indemnify AstroA2Z against any claims arising from your services,
              conduct, or negligence.
            </p>
          </section>

          <section>
            <h2>12. Termination</h2>
            <ul>
              <li>Either party may terminate this arrangement with 7 days' written notice</li>
              <li>AstroA2Z may immediately terminate for serious violations (fraud, harassment, illegal activity)</li>
              <li>Upon termination, pending payouts for completed services will still be processed</li>
              <li>Your profile and reviews will be deactivated (soft-deleted)</li>
            </ul>
          </section>

          <section>
            <h2>13. Dispute Resolution</h2>
            <p>
              Disputes between you and users will be mediated by AstroA2Z in the first instance.
              Unresolved disputes are subject to the exclusive jurisdiction of courts in Hyderabad,
              Telangana, India.
            </p>
          </section>

          <section>
            <h2>14. Amendments</h2>
            <p>
              AstroA2Z may amend these Priest Terms at any time. Material changes will be communicated
              via email. Continued use of the Platform after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2>15. General Terms</h2>
            <p>
              All general <Link to="/terms-and-conditions">Terms &amp; Conditions</Link> and{' '}
              <Link to="/privacy-policy">Privacy Policy</Link> of AstroA2Z also apply to you in
              addition to these Priest Terms.
            </p>
          </section>

          <section>
            <h2>16. Contact</h2>
            <p>
              <strong>Priest Support:</strong>{' '}
              <a href="mailto:priests@astroa2z.com">priests@astroa2z.com</a><br />
              <strong>Grievance Officer:</strong>{' '}
              <a href="mailto:grievance@astroa2z.com">grievance@astroa2z.com</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
